import { useMemo } from 'react';
import type { ParticipantVideoOverlayProps } from './ParticipantVideo';
import { useCallTileOrder } from '../hooks/useCallTileOrder';

/** What the sidebar and gallery layouts both take from the controller to draw call tiles (SC-375). */
export interface CallTileLayoutProps {
  participants: any[];
  screenShareTracks: any[];
  tracks: any[];
  speakingIds: Set<string>;
  isParticipantVisibleWithGrace: (id: string) => boolean;
  isParticipantInSameRoom: (id: string) => boolean;
  handleScreenShareClick: (participantId: string, trackSid: string) => void;
  videoDisplayState: any;
  videoDisplayActions: any;
  ownerSessionId?: string;
  whiteboardUserSessionIds?: Set<string>;
  onWhiteboardClick?: (participantId: string) => void;
  handRaisedSessionIds?: Set<string>;
  /** Set only when the local user may lower other people's hands */
  onLowerHand?: (participantId: string) => void;
}

const NO_RAISED_HANDS: Set<string> = new Set();
const noHover = () => { };

/**
 * Tile order plus a builder for each tile's props, shared by both layouts so a tile prop added for one
 * cannot be forgotten in the other. `getViewportCapacity` is how many tiles are on screen right now.
 */
export function useCallTiles(
  layout: CallTileLayoutProps,
  getViewportCapacity: (tileCount: number) => number
): {
  order: string[];
  screenShareParticipantIds: Set<string>;
  tileProps: (participant: any) => ParticipantVideoOverlayProps;
} {
  const {
    participants,
    screenShareTracks,
    tracks,
    speakingIds,
    isParticipantVisibleWithGrace,
    isParticipantInSameRoom,
    handleScreenShareClick,
    videoDisplayState,
    videoDisplayActions,
    ownerSessionId,
    whiteboardUserSessionIds,
    onWhiteboardClick,
    handRaisedSessionIds = NO_RAISED_HANDS,
    onLowerHand,
  } = layout;

  const screenShareParticipantIds = useMemo(
    () => new Set<string>(screenShareTracks.map((track) => track.participant.identity)),
    [screenShareTracks]
  );

  const order = useCallTileOrder({
    participants,
    screenShareParticipantIds,
    speakingIds,
    handRaisedSessionIds,
    isParticipantVisibleWithGrace,
    isParticipantInSameRoom,
    getViewportCapacity,
  });

  const tileProps = (participant: any): ParticipantVideoOverlayProps => {
    const participantId: string = participant.identity;
    const isLocal = Boolean(participant.isLocal);
    return {
      participantId,
      isLocal,
      isSpeaking: speakingIds.has(participantId),
      isScreenSharing: screenShareParticipantIds.has(participantId),
      isExpanded: false,
      onHover: noHover,
      onScreenShareClick: handleScreenShareClick,
      videoControls: { ...videoDisplayState, ...videoDisplayActions },
      participants,
      screenShareTracks,
      cameraTrack: tracks.find((t) => t.participant.identity === participantId),
      ownerSessionId,
      isUsingWhiteboard: whiteboardUserSessionIds?.has(participantId),
      onWhiteboardClick,
      isHandRaised: handRaisedSessionIds.has(participantId),
      onLowerHand: isLocal ? undefined : onLowerHand,
    };
  };

  return { order, screenShareParticipantIds, tileProps };
}
